import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Pencil, Plus, RefreshCw, Save, Trash2, Upload } from 'lucide-react'
import { slidersApi } from '../../services/api'
import Button from '../../components/ui/Button'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import Card from '../../components/ui/Card'
import Modal from '../../components/ui/Modal'
import AdminFormField, { adminInputClass } from '../../components/admin/AdminFormField'
import MediaImage from '../../components/ui/MediaImage'
import { previewImageSrc, resolveMediaUrl, stripStoragePrefix } from '../../utils/media'

const MAX_SLIDER_IMAGE_BYTES = 10 * 1024 * 1024

const EMPTY = {
  hero_type: 'image',
  label_en: '',
  label_ar: '',
  title_en: '',
  title_ar: '',
  subtitle_en: '',
  subtitle_ar: '',
  image: '',
  video_url: '',
  video_file: '',
  sort_order: 0,
  is_active: true,
}

function buildSliderFormData(form, imageFile) {
  const fd = new FormData()
  const fields = [
    'hero_type',
    'label_en',
    'label_ar',
    'title_en',
    'title_ar',
    'subtitle_en',
    'subtitle_ar',
    'video_url',
    'video_file',
  ]

  fields.forEach((field) => {
    const value = form[field]
    if (value != null && value !== '') {
      fd.append(field, value)
    }
  })

  fd.append('sort_order', String(form.sort_order ?? form.order ?? 0))
  fd.append('is_active', form.is_active ?? form.active ? '1' : '0')

  if (imageFile) {
    fd.append('image', imageFile)
  } else if (form.image) {
    fd.append('image', stripStoragePrefix(form.image))
  }

  return fd
}

function getSlideTopLabel(slide, isAr) {
  if (isAr) return slide.label_ar || slide.label_en || ''
  return slide.label_en || slide.label_ar || ''
}

function getSlideLabel(slide, isAr) {
  if (isAr) return slide.title_ar || slide.title_en || `#${slide.sort_order}`
  return slide.title_en || slide.title_ar || `#${slide.sort_order}`
}

function getSlideSubtitle(slide, isAr) {
  if (isAr) return slide.subtitle_ar || slide.subtitle_en || ''
  return slide.subtitle_en || slide.subtitle_ar || ''
}

function sortSlides(list) {
  return list.slice().sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
}

function SlideEditor({ slide, isNew, onSaved, onDeleted, onCancel }) {
  const { t } = useTranslation()
  const [form, setForm] = useState(slide ? { ...EMPTY, ...slide, hero_type: slide.hero_type || 'image' } : { ...EMPTY })
  const [imageFile, setImageFile] = useState(null)
  const [imagePreview, setImagePreview] = useState(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const fileRef = useRef(null)

  useEffect(() => {
    setForm(slide ? { ...EMPTY, ...slide, hero_type: slide.hero_type || 'image' } : { ...EMPTY })
    if (imagePreview?.startsWith('blob:')) URL.revokeObjectURL(imagePreview)
    setImageFile(null)
    setImagePreview(null)
    setError('')
  }, [slide])

  useEffect(() => {
    return () => {
      if (imagePreview?.startsWith('blob:')) URL.revokeObjectURL(imagePreview)
    }
  }, [imagePreview])

  const handleImageSelect = (event) => {
    const file = event.target.files?.[0]
    if (!file) return
    if (file.size > MAX_SLIDER_IMAGE_BYTES) {
      setError(t('admin.slidersAdmin.imageTooLarge'))
      event.target.value = ''
      return
    }
    if (imagePreview?.startsWith('blob:')) URL.revokeObjectURL(imagePreview)
    setImageFile(file)
    setImagePreview(URL.createObjectURL(file))
    setError('')
    event.target.value = ''
  }

  const handleSave = async (e) => {
    e.preventDefault()
    setSaving(true)
    setError('')
    try {
      if (imageFile && imageFile.size > MAX_SLIDER_IMAGE_BYTES) {
        setError(t('admin.slidersAdmin.imageTooLarge'))
        return
      }
      const payload = buildSliderFormData(form, imageFile)
      if (form.id) await slidersApi.update(form.id, payload)
      else await slidersApi.create(payload)
      onSaved()
    } catch (err) {
      const validationError = Object.values(err.response?.data?.errors || {}).flat()?.[0]
      const message =
        validationError ||
        err.response?.data?.message ||
        t('admin.slidersAdmin.saveFailed')
      setError(message)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!form.id || !window.confirm(t('admin.slidersAdmin.confirmDelete'))) return
    await slidersApi.delete(form.id)
    onDeleted()
  }

  const currentPreview = imagePreview || previewImageSrc(form.image)

  return (
    <form onSubmit={handleSave} className="space-y-4">
        <div>
          <label className="mb-2 block text-sm font-medium">{t('admin.slidersAdmin.heroType')}</label>
          <div className="flex gap-4">
            {['image', 'video'].map((type) => (
              <label key={type} className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="hero_type"
                  checked={form.hero_type === type}
                  onChange={() => setForm({ ...form, hero_type: type })}
                />
                {t(`admin.slidersAdmin.type.${type}`)}
              </label>
            ))}
          </div>
        </div>

        <div className="space-y-5">
          <div>
            <h3 className="mb-3 text-sm font-semibold text-primary">{t('admin.slidersAdmin.textTopLabel')}</h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <AdminFormField label={t('admin.slidersAdmin.badgeLabelEn')} htmlFor="slider-label-en">
                <input id="slider-label-en" value={form.label_en || ''} onChange={(e) => setForm({ ...form, label_en: e.target.value })} className={adminInputClass} placeholder="Home of the Intellectuals" />
              </AdminFormField>
              <AdminFormField label={t('admin.slidersAdmin.badgeLabelAr')} htmlFor="slider-label-ar">
                <input id="slider-label-ar" value={form.label_ar || ''} onChange={(e) => setForm({ ...form, label_ar: e.target.value })} dir="rtl" lang="ar" className={`${adminInputClass} font-ar`} placeholder="موطن المثقفين" />
              </AdminFormField>
            </div>
          </div>

          <div>
            <h3 className="mb-3 text-sm font-semibold text-primary">{t('admin.slidersAdmin.textMainTitle')}</h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <AdminFormField label={t('admin.pages.titleEn')} htmlFor="slider-title-en">
                <input id="slider-title-en" value={form.title_en} onChange={(e) => setForm({ ...form, title_en: e.target.value })} className={adminInputClass} required />
              </AdminFormField>
              <AdminFormField label={t('admin.pages.titleAr')} htmlFor="slider-title-ar">
                <input id="slider-title-ar" value={form.title_ar} onChange={(e) => setForm({ ...form, title_ar: e.target.value })} dir="rtl" lang="ar" className={`${adminInputClass} font-ar`} required />
              </AdminFormField>
            </div>
          </div>

          <div>
            <h3 className="mb-3 text-sm font-semibold text-primary">{t('admin.slidersAdmin.textDescription')}</h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <AdminFormField label={t('admin.slidersAdmin.subtitleEn')} htmlFor="slider-subtitle-en">
                <textarea id="slider-subtitle-en" rows={3} value={form.subtitle_en || ''} onChange={(e) => setForm({ ...form, subtitle_en: e.target.value })} className={adminInputClass} />
              </AdminFormField>
              <AdminFormField label={t('admin.slidersAdmin.subtitleAr')} htmlFor="slider-subtitle-ar">
                <textarea id="slider-subtitle-ar" rows={3} value={form.subtitle_ar || ''} onChange={(e) => setForm({ ...form, subtitle_ar: e.target.value })} dir="rtl" lang="ar" className={`${adminInputClass} font-ar`} />
              </AdminFormField>
            </div>
          </div>
        </div>

        {form.hero_type === 'image' ? (
          <AdminFormField label={t('admin.slidersAdmin.image')} htmlFor="slider-image">
            <div className="space-y-3">
              <p className="text-xs text-text/60">{t('admin.slidersAdmin.imageHint')}</p>
              {currentPreview ? (
                <MediaImage src={currentPreview} alt="" className="h-40 w-full rounded-xl border border-border object-cover" fallbackIndex={0} />
              ) : null}
              <input id="slider-image" value={form.image || ''} onChange={(e) => setForm({ ...form, image: e.target.value })} placeholder={t('admin.slidersAdmin.imageUrl')} className={adminInputClass} />
              <div className="flex flex-wrap items-center gap-3">
                <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/jpg" className="hidden" onChange={handleImageSelect} />
                <Button type="button" variant="outlineDark" size="sm" onClick={() => fileRef.current?.click()} disabled={saving}>
                  <Upload className="h-4 w-4" />
                  {saving && imageFile ? t('admin.slidersAdmin.uploadingImage') : t('admin.slidersAdmin.uploadImage')}
                </Button>
                {imageFile ? <span className="text-xs text-text/60">{imageFile.name}</span> : null}
              </div>
            </div>
          </AdminFormField>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            <AdminFormField label={t('admin.slidersAdmin.videoUrl')} htmlFor="slider-video-url" className="sm:col-span-2">
              <input id="slider-video-url" value={form.video_url || ''} onChange={(e) => setForm({ ...form, video_url: e.target.value })} className={adminInputClass} />
            </AdminFormField>
            <AdminFormField label={t('admin.slidersAdmin.videoFile')} htmlFor="slider-video-file" className="sm:col-span-2">
              <input id="slider-video-file" value={form.video_file || ''} onChange={(e) => setForm({ ...form, video_file: e.target.value })} className={adminInputClass} />
            </AdminFormField>
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <AdminFormField label={t('admin.slidersAdmin.order')} htmlFor="slider-order">
            <input id="slider-order" type="number" value={form.sort_order ?? form.order ?? 0} onChange={(e) => setForm({ ...form, sort_order: +e.target.value, order: +e.target.value })} className={adminInputClass} />
          </AdminFormField>
          <label className="flex items-center gap-2 self-end pb-2 text-sm">
            <input type="checkbox" checked={Boolean(form.is_active ?? form.active)} onChange={(e) => setForm({ ...form, is_active: e.target.checked, active: e.target.checked })} />
            {t('admin.menus.active')}
          </label>
        </div>

        {error ? <p className="text-sm text-red-600">{error}</p> : null}

        <div className="flex flex-wrap items-center gap-2 border-t border-border pt-4">
          <Button type="submit" variant="primary" disabled={saving}>
            <Save className="h-4 w-4" />
            {t('buttons.save')}
          </Button>
          <Button type="button" variant="ghost" onClick={onCancel}>
            {t('buttons.cancel')}
          </Button>
          {!isNew ? (
            <Button type="button" variant="ghost" onClick={handleDelete} className="text-red-600">
              <Trash2 className="h-4 w-4" />
              {t('buttons.delete')}
            </Button>
          ) : null}
        </div>
      </form>
  )
}

function SlideCard({ item, isAr, isEditing, onEdit, onDelete }) {
  const { t } = useTranslation()

  return (
    <Card
      padding={false}
      className={`overflow-hidden transition-shadow ${isEditing ? 'ring-2 ring-primary' : 'hover:shadow-md'}`}
    >
      {item.hero_type === 'video' ? (
        <div className="flex h-48 items-center justify-center bg-primary/5 text-sm text-primary">
          {t('admin.slidersAdmin.type.video')}
        </div>
      ) : (
        <MediaImage
          src={item.image}
          alt={item.title_en || ''}
          className="h-32 w-full object-cover"
          fallbackIndex={(item.sort_order ?? 1) - 1}
        />
      )}
      <div className="p-3">
        <div className="mb-1.5 flex items-center gap-2">
          <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold text-primary">
            #{item.sort_order ?? 0}
          </span>
          <span
            className={`h-1.5 w-1.5 rounded-full ${item.is_active ? 'bg-green-500' : 'bg-gray-300'}`}
            title={item.is_active ? t('admin.homepage.active') : t('admin.homepage.inactive')}
          />
        </div>
        {getSlideTopLabel(item, isAr) ? (
          <span className="mb-1.5 inline-block max-w-full truncate rounded-full bg-accent/90 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white">
            {getSlideTopLabel(item, isAr)}
          </span>
        ) : null}
        <h3 className="line-clamp-2 text-sm font-semibold text-primary">{getSlideLabel(item, isAr)}</h3>
        <p className="mt-1 line-clamp-2 text-xs text-text/60">{getSlideSubtitle(item, isAr)}</p>
        <div className="mt-3 flex gap-1.5">
          <Button type="button" variant={isEditing ? 'primary' : 'outlineDark'} size="sm" className="h-8 px-2.5 text-xs" onClick={onEdit}>
            <Pencil className="h-3.5 w-3.5" />
            {t('admin.slidersAdmin.edit')}
          </Button>
          <Button type="button" variant="ghost" size="sm" className="h-8 px-2 text-red-600" onClick={onDelete}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </Card>
  )
}

export default function AdminSliders() {
  const { t, i18n } = useTranslation()
  const isAr = i18n.language === 'ar'
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [editingSlide, setEditingSlide] = useState(null)
  const [creating, setCreating] = useState(false)
  const autoSyncedRef = useRef(false)
  const isEditorOpen = Boolean(creating || editingSlide)

  const load = async () => {
    setLoading(true)
    try {
      let list = []

      try {
        const { data } = await slidersApi.getAllAdmin()
        list = sortSlides(data.data || data || [])
      } catch {
        const { data } = await slidersApi.getAll()
        list = sortSlides(data.data || data || [])
      }

      if (list.length === 0 && !autoSyncedRef.current) {
        autoSyncedRef.current = true
        try {
          const synced = await slidersApi.syncHomepage()
          list = sortSlides(synced.data?.data || synced.data || [])
        } catch {
          /* sync unavailable — fall through */
        }
      }

      setItems(list)
    } catch {
      setItems([])
    } finally {
      setLoading(false)
    }
  }

  const handleSync = async () => {
    if (!window.confirm(t('admin.slidersAdmin.confirmSync'))) return
    setLoading(true)
    try {
      const synced = await slidersApi.syncHomepage()
      const list = sortSlides(synced.data?.data || synced.data || [])
      setItems(list)
      setCreating(false)
      setEditingSlide(null)
    } catch {
      setItems([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const openEdit = (slide) => {
    setCreating(false)
    setEditingSlide(slide)
  }

  const openCreate = () => {
    setCreating(true)
    setEditingSlide({ ...EMPTY, sort_order: items.length + 1 })
  }

  const closeEditor = () => {
    setCreating(false)
    setEditingSlide(null)
  }

  const handleDelete = async (id) => {
    if (!window.confirm(t('admin.slidersAdmin.confirmDelete'))) return
    await slidersApi.delete(id)
    if (editingSlide?.id === id) closeEditor()
    load()
  }

  const handleSaved = () => {
    closeEditor()
    load()
  }

  return (
    <div className="mx-auto max-w-7xl">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-2xl font-bold text-primary">{t('admin.slidersAdmin.title')}</h1>
        <div className="flex flex-wrap gap-2">
          <Button variant="ghost" size="sm" onClick={load}>
            <RefreshCw className="h-4 w-4" />
            {t('admin.homepage.refresh')}
          </Button>
          <Button variant="outlineDark" size="sm" onClick={handleSync}>
            <RefreshCw className="h-4 w-4" />
            {t('admin.slidersAdmin.syncHomepage')}
          </Button>
          <Button variant="accent" size="sm" onClick={openCreate}>
            <Plus className="h-4 w-4" />
            {t('admin.slidersAdmin.add')}
          </Button>
        </div>
      </div>

      {loading ? (
        <LoadingSpinner className="py-20" />
      ) : items.length === 0 ? (
        <Card className="py-16 text-center">
          <p className="mb-4 text-sm text-text/60">{t('admin.slidersAdmin.emptySlides')}</p>
          <div className="flex flex-wrap justify-center gap-2">
            <Button variant="primary" onClick={handleSync}>
              <RefreshCw className="h-4 w-4" />
              {t('admin.slidersAdmin.syncHomepage')}
            </Button>
            <Button variant="outlineDark" onClick={openCreate}>
              <Plus className="h-4 w-4" />
              {t('admin.slidersAdmin.add')}
            </Button>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((item) => (
            <SlideCard
              key={item.id}
              item={item}
              isAr={isAr}
              isEditing={!creating && editingSlide?.id === item.id}
              onEdit={() => openEdit(item)}
              onDelete={() => handleDelete(item.id)}
            />
          ))}
        </div>
      )}

      <Modal
        isOpen={isEditorOpen}
        onClose={closeEditor}
        title={creating ? t('admin.slidersAdmin.add') : t('admin.slidersAdmin.edit')}
        maxWidth="max-w-3xl"
      >
        {isEditorOpen && (
          <SlideEditor
            key={creating ? 'new' : editingSlide.id}
            slide={editingSlide}
            isNew={creating}
            onSaved={handleSaved}
            onDeleted={() => {
              closeEditor()
              load()
            }}
            onCancel={closeEditor}
          />
        )}
      </Modal>
    </div>
  )
}
